// src/services/statutesService.js
import { fetchWithAuth } from "../utils/fetchWithAuth";

const BASE = "/statutes";

// ---------- Utils ----------
const readJsonSafe = async (res) => {
  const ct = res.headers?.get?.("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  const text = await res.text().catch(() => "");
  try { return JSON.parse(text); } catch { return text || null; }
};
const textSafe = async (res) => await res.text().catch(() => "");
const err = async (method, url, res) => {
  const text = await textSafe(res);
  throw new Error(`${method} ${url} ${res.status} ${res.statusText} ${text}`.trim());
};
const isObj = (v) => v && typeof v === "object";
const extractRejectedKeys = (payload, body) => {
  const bad = [];
  if (!isObj(body)) return bad;
  const msg = (body.message || body.error || "").toString().toLowerCase();
  Object.keys(payload).forEach(k => {
    if (msg.includes(k.toLowerCase())) bad.push(k);
  });
  return bad;
};

// ---------- Validaciones ----------
export const isValidCtaUrl = (s) => {
  if (!s) return true;
  if (s.startsWith("mailto:")) return s.slice(7).includes("@");
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch { return false; }
};

// ---------- Listados ----------
export const listAdminStatutes = async (year) => {
  const q = year ? `?year=${encodeURIComponent(year)}` : "";
  const url = `${BASE}${q}`;
  const res = await fetchWithAuth(url, { method: "GET" });
  if (!res.ok) await err("GET", url, res);
  return readJsonSafe(res); // { items, config }
};

export const listStatutes = async (year) => {
  const data = await listAdminStatutes(year);
  return Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
};

export const listPublicStatutes = async (year) => {
  const q = year ? `?year=${encodeURIComponent(year)}` : "";
  const url = `${BASE}/public${q}`;
  const res = await fetchWithAuth(url, { method: "GET" });
  if (!res.ok) await err("GET", url, res);
  return readJsonSafe(res); // { items, config }
};

// ---------- Upload ----------
export const uploadStatute = async ({ file, title, version, year, visibleForMembers, featuredInHomepage }) => {
  if (!file) throw new Error("Falta seleccionar un archivo.");
  if (!title?.trim()) throw new Error("Falta el título del documento.");

  const form = new FormData();
  form.append("file", file);
  form.append("title", title.trim());
  if (version) form.append("version", String(version).trim());
  if (Number.isFinite(Number(year))) form.append("year", String(Number(year)));
  if (typeof visibleForMembers === "boolean") form.append("visibleForMembers", String(visibleForMembers));
  if (typeof featuredInHomepage === "boolean") form.append("featuredInHomepage", String(featuredInHomepage));

  const url = `${BASE}`;
  const res = await fetchWithAuth(url, { method: "POST", body: form });
  if (!res.ok) await err("POST", url, res);
  return readJsonSafe(res);
};

// ---------- Edición / eliminación ----------
export const updateStatute = async (id, patch = {}) => {
  const url = `${BASE}/${encodeURIComponent(id)}`;
  const res = await fetchWithAuth(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) await err("PATCH", url, res);
  return readJsonSafe(res);
};

export const deleteStatute = async (id) => {
  const url = `${BASE}/${encodeURIComponent(id)}`;
  const res = await fetchWithAuth(url, { method: "DELETE" });
  if (!res.ok) await err("DELETE", url, res);
  return readJsonSafe(res);
};

// 👇 Alias para que StatutesReportsCard funcione con Svc.removeStatute
export const removeStatute = deleteStatute;

export const reorderByYear = async (year, ids) => {
  const url = `${BASE}/reorder`;
  const res = await fetchWithAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ year, ids: Array.isArray(ids) ? ids : [] }),
  });
  if (!res.ok) await err("POST", url, res);
  return readJsonSafe(res);
};

// ---------- Config ----------
export const getStatutesConfig = async () => {
  const url = `${BASE}/config`;
  const res = await fetchWithAuth(url, { method: "GET" });
  if (!res.ok) await err("GET", url, res);
  return readJsonSafe(res);
};

export const updateStatutesConfig = async ({
  principlesVisible,
  ctaEnabled,
  ctaLabel,
  ctaUrl,
  lastVisibleAt,
  pageTitle,
  subtitle,
  previousTitle,
} = {}) => {
  // Validación CTA
  if (ctaEnabled) {
    if (!ctaLabel?.trim()) throw new Error("CTA: falta el nombre del botón.");
    if (!isValidCtaUrl(ctaUrl || "")) throw new Error("CTA: la URL debe empezar con http(s):// o mailto:");
  }

  const fullPayload = {};
  if (typeof principlesVisible === "boolean") fullPayload.principlesVisible = principlesVisible;
  if (typeof ctaEnabled === "boolean") fullPayload.ctaEnabled = ctaEnabled;
  if (ctaLabel != null) fullPayload.ctaLabel = String(ctaLabel).trim();
  if (ctaUrl != null) fullPayload.ctaUrl = String(ctaUrl).trim();
  if (typeof lastVisibleAt !== "undefined") fullPayload.lastVisibleAt = lastVisibleAt;
  if (pageTitle != null) fullPayload.pageTitle = String(pageTitle).trim();
  if (subtitle != null) fullPayload.subtitle = String(subtitle).trim();
  if (previousTitle != null) fullPayload.previousTitle = String(previousTitle).trim();

  const tryOnce = async (method, payload) => {
    const url = `${BASE}/config`;
    const res = await fetchWithAuth(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return { url, res };
  };

  // PATCH → PUT de respaldo + filtrado de campos rechazados por backends antiguos
  let payload = { ...fullPayload };
  let method = "PATCH";
  const triedMethods = new Set();
  for (let attempt = 0; attempt < 6; attempt++) {
    const { url, res } = await tryOnce(method, payload);
    if (res.ok) return readJsonSafe(res);
    const body = await readJsonSafe(res);
    if (res.status === 405 && !triedMethods.has("PUT")) { triedMethods.add("PATCH"); method = "PUT"; continue; }
    if (res.status === 400) {
      const bad = extractRejectedKeys(payload, body);
      if (bad.length) { bad.forEach(k => delete payload[k]); if (!Object.keys(payload).length) throw new Error("El backend no acepta ninguno de los campos de configuración enviados."); continue; }
    }
    await err(method, `${BASE}/config`, res);
  }
  throw new Error("No fue posible actualizar la configuración.");
};

// ---------- Titulares por año ----------
export const getYearHeadline = async (year) => {
  const url = `${BASE}/year/${encodeURIComponent(year)}/headline`;
  const res = await fetchWithAuth(url, { method: "GET" });
  if (!res.ok) await err("GET", url, res);
  return readJsonSafe(res);
};

export const saveYearHeadline = async (year, headline) => {
  const url = `${BASE}/year/${encodeURIComponent(year)}/headline`;
  const res = await fetchWithAuth(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ headline }),
  });
  if (!res.ok) await err("PATCH", url, res);
  return readJsonSafe(res);
};

export const downloadStatuteUrl = (id) =>
  id ? `${BASE}/${encodeURIComponent(id)}/download` : "#";
